/* eslint-disable max-len */
import sdk from '@/sdk';
import {SinglePersonData} from '@/interface';
import assert from 'assert';
import {InheritRepo, MemberSearchType, MemberType, Role} from '@/Constant';
import {BOOK_TYPE} from '@/constants/document/doc';

export function getRoleName(member: SinglePersonData, memberList: SinglePersonData[]) {
    let memberId: string | number = '';
    if (member.memberType === MemberType.User) {
        memberId = member.userGuid || '';
    } else if (member.memberType === MemberType.EmailGroup) {
        memberId = member.emailGroupAddress || '';
    } else {
        memberId = member.gid || '';
    }
    const hitMember = memberList.find((data: SinglePersonData) => {
        return `${data.memberGuid}` === `${memberId}`;
    });
    if (!hitMember || hitMember.status === 15) {
        return '';
    }
    return hitMember.roleName || '';
}

export async function onSearch(
    value: string,
    repository?: Repository,
    docGuid?: string,
    isShare?: boolean,
    curDocument?: WizDocument | null,
    isSearchSensitiveUser?: boolean,
    isShowVirtualList?: boolean,
    queryExternalUser?: boolean
) {
    try {
        const searchType = isShare ? `${MemberSearchType.User},${MemberSearchType.Group}` : MemberSearchType.All;
        const personList = await sdk.as.getSearchPerson(value, searchType, !!isSearchSensitiveUser, queryExternalUser);
        const virtualListRes = await sdk.as.getSearchVirtual(value);
        let virtualList: SinglePersonData[] = [];
        if (virtualListRes.result && isShowVirtualList) {
            virtualList = virtualListRes.result.map((virtualData: SinglePersonData) => {
                return {
                    ...virtualData,
                    memberType: 5,
                };
            });
        }

        const allList: SinglePersonData[] = personList.result.concat(virtualList);
        // eslint-disable-next-line max-len
        const uniqueArray = allList.filter((item: SinglePersonData, index: number, self: SinglePersonData[]) => {
            // 当avatar为空时直接保留，不为空时执行原有去重逻辑
            return !item.avatar ? true : index === self.findIndex(t => t.avatar === item.avatar);
        });

        personList.result = uniqueArray;

        // @todo shareModal 需要重构 减少业务代码侵入
        if (!repository && !docGuid) {
            return personList;
        }
        const params = personList.result.map((val: SinglePersonData) => {
            if (val.memberType === MemberType.User) {
                return val.userGuid;
            } else if (val.memberType === MemberType.EmailGroup) {
                return val.emailGroupAddress;
            } else if ('isClosed' in val) {
                return val.userGuid;
            } else if (val?.isVirtualUser) {
                return val.userGuid;
            } else {
                return val.gid;
            }
        }).join(',');
        assert(repository);
        if (params) {
            let personPowerList: any = [];
            let identityList: any = [];
            let directoryRoleList: any = [];
            const ksServer = await sdk.getKsServer(repository);
            personPowerList = docGuid ? await ksServer.getHavePowerPerson(docGuid, params) : [];
            identityList = await sdk.as.getIdentityPersonList(repository, params);
            // 知识本下文档获取知识本权限
            // so-kldoc-12594 [Epic] 【研发优化】成员管理新增成员搜索列表展示失败
            // 问题的本质是在切换到非文档页面的时候curDocument没有重置，导致了请求失败
            // 所以得判断docGuid(是从业务中透传过来的不是实时从store拿的值)是否存在
            if (docGuid && curDocument?.bookType === BOOK_TYPE.IN_BOOK && curDocument?.rootGuid) {
                directoryRoleList = await ksServer.getHavePowerPerson(curDocument?.rootGuid, params);
            }
            personList.result = personList.result.map((member: SinglePersonData) => {
                const rocRoleName = getRoleName(member, personPowerList);
                let roleName = getRoleName(member, identityList);
                const directoryRoleName = getRoleName(member, directoryRoleList);
                if (curDocument?.inheritRepo === InheritRepo.Not_Inherit &&
                    (roleName === Role.Member || roleName === Role.Reader)) {
                    roleName = '';
                }
                return {
                    ...member,
                    rocRoleName,
                    roleName,
                    directoryRoleName,
                };
            });
        }
        return personList;
    } catch (error) {
        return error;
    }
}

// 文档和知识库对最近@人的数据处理
export async function mergeMemberIdentity(
    dataList: any,
    repository?: Repository,
    docGuid?: string
) {
    try {
        const params = dataList.map((val: any) => {
            return val.memberGuid || val.userGuid;
        }).join(',');
        const identityMember = await sdk.as.getIdentityPersonList(repository, params);
        if (params) {
            const personList = await sdk.as.getMemberInfo(params);
            const ksServer = await sdk.getKsServer(repository);
            const personPowerList = docGuid ? await ksServer.getHavePowerPerson(docGuid, params) : [];
            // eslint-disable-next-line complexity
            dataList.forEach((val: any) => {
                for (const index in identityMember) {
                    if ((val.memberGuid || val.userGuid) === identityMember[index].memberGuid) {
                        val.roleName = identityMember[index]?.roleName;
                        val.memberType = identityMember[index]?.memberType;
                        val.repoRoleStatus = identityMember[index]?.status;
                        break;
                    }
                }
                for (const index in personList) {
                    if ((val.memberGuid || val.userGuid) === personList[index].userGuid) {
                        val.email = 'email' in personList[index] ? personList[index]?.email : '';
                        val.gid = 'gid' in personList[index] ? personList[index]?.gid : '';
                        val.department = 'department' in personList[index] ? personList[index]?.department : '';
                        val.userGuid = 'userGuid' in personList[index] ? personList[index]?.userGuid : '';
                        val.external = 'external' in personList[index] ? personList[index]?.external : '';
                        val.passportDisplayName = 'passportDisplayName' in personList[index] ? personList[index]?.passportDisplayName : '';
                    }
                }
                for (const index in personPowerList) {
                    if ((val.memberGuid || val.userGuid) === personPowerList[index].memberGuid) {
                        val.rocRoleName = personPowerList[index]?.roleName;
                        break;
                    }
                }
            });
        }
        return dataList;
    } catch (error) {
        return error;
    }
}

// TODO: 部门情况下的搜索，后续再精简抽离
export async function departmentOnSearch(
    value: string,
    spaceGuid?: string,
    groupGuid?: string,
    isShare?: boolean,
    querySensitiveUser?: boolean
) {
    try {
        const searchType = isShare ? `${MemberSearchType.User},${MemberSearchType.Group}` : MemberSearchType.All;
        const personList = await sdk.as.getSearchPerson(value, searchType, querySensitiveUser);
        const params = personList.result.map((val: any) => {
            if (val.memberType === MemberType.User) {
                return val.userGuid;
            } else if (val.memberType === MemberType.EmailGroup) {
                return val.emailGroupAddress;
            } else {
                return val.gid;
            }
        }).join(',');
        const identityList = await sdk.as.searchDepartmentMember(
            {
                memberGuids: params,
            },
            {
                spaceGuid: spaceGuid || '',
                groupGuid: groupGuid || '',
            }
        );
        personList.result = personList.result.map((val: any) => {
            let memberGuid: string | number = '';
            if (val.memberType === MemberType.User) {
                memberGuid = val.userGuid || '';
            } else if (val.memberType === MemberType.EmailGroup) {
                memberGuid = val.emailGroupAddress || '';
            } else {
                memberGuid = `${val.gid}`;
            }
            const hitMember = identityList.find((data: SinglePersonData) => {
                return data.memberGuid === memberGuid;
            });
            if (hitMember) {
                const roleName = hitMember.status === 15 ? '' : hitMember.roleName;
                return {
                    ...val,
                    roleName,
                };
            }
            return val;
        });
        return personList;
    } catch (error) {
        console.log(error);
        return error;
    }
}


// TODO: 部门情况下对最近@人查询权限情况, 后续再精简抽离
export async function mergeDepartmentIdentity(personList: any, spaceGuid?: string, groupGuid?: string) {
    const params = personList.map((val: any) => {
        // 最近@人没有群，也没有memberType，只有userGuid，后续让server针对@人接口优化
        return val.userGuid;
    }).join(',');
    // 对最近@人查询部门权限信息
    const resultData = await sdk.as.searchDepartmentMember({
        memberGuids: params,
    }, {
        spaceGuid: spaceGuid || '',
        groupGuid: groupGuid || '',
    });
    personList = personList.map((val: any) => {
        const hitMember = resultData.find((data: SinglePersonData) => {
            return data.memberGuid === val.userGuid;
        });
        if (hitMember) {
            const roleName = hitMember.status === 15 ? '' : hitMember.roleName;
            return {
                ...val,
                roleName,
            };
        }
        return val;
    });
    return personList;
}

